/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-01-19 10:01:40
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { FormLayout, Icon, TextStyle, Link } from '@shopify/polaris';
import { ExternalMinor } from '@shopify/polaris-icons';
import { Field } from 'redux-form';
import { RenderSelect2 } from 'src/components/views/common/forms/render-select-2-v2';
import { RenderTextField } from 'src/components/views/common/forms/render-textfield';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import { RenderRadioButton2 } from 'src/components/views/common/forms/render-radio-button-2';
import {
  PRODUCT_IMAGE_RATIO_VALUE,
  PRODUCT_IMAGE_RATIO_OPTIONS,
  PRODUCT_IMAGE_CROP_IMAGE_POSITION_OPTIONS,
  PRODUCT_IMAGE_HOVER_EFFECT_OPTIONS
} from 'src/constants/theme-settings';
import { isRatioV2, isRatioV2Bool } from 'src/helpers/form-validation-helper';
import { DropdownIndicator, styles } from './react-select-style';
import { URL } from 'src/constants/links';
import {
  MENU_ID,
  PRODUCT_INFORMATION_ALIGNMENT_OPTIONS_TEXT
} from 'src/constants/theme-settings.jsx';

type Props = {
  formValues: any,
  prefix: string,
  updateField: (field: string, value: any) => void
};

type ComposedProps = Props & WithI18nProps;

const PRODUCT_IMAGE_RATIO_OPTIONS_SI = _.cloneDeep(PRODUCT_IMAGE_RATIO_OPTIONS);
PRODUCT_IMAGE_RATIO_OPTIONS_SI.pop();

class ProductImageForm extends Component<ComposedProps> {
  componentWillReceiveProps(nextProps) {
    const { formValues } = this.props;
    const { formValues: nextFormValues, updateField } = nextProps;
    const productImageRatio = _.get(
      formValues,
      `setting.${MENU_ID.PRODUCT_IMAGE}.aspectRatioType`,
      ''
    );
    const nextProductImageRatio = _.get(
      nextFormValues,
      `setting.${MENU_ID.PRODUCT_IMAGE}.aspectRatioType`,
      ''
    );
    const manualAspectRatio = _.get(
      nextFormValues,
      `setting.${MENU_ID.PRODUCT_IMAGE}.manualAspectRatio`,
      ''
    );

    // Clear value when select ratio type which is different from "aspectRatioType" while "manualAspectRatio" is invalid
    if (
      productImageRatio !== nextProductImageRatio &&
      productImageRatio === PRODUCT_IMAGE_RATIO_VALUE.MANUAL &&
      !isRatioV2Bool(manualAspectRatio)
    ) {
      updateField(`setting.${MENU_ID.PRODUCT_IMAGE}.manualAspectRatio`, '');
    }
  }

  componentWillUnmount() {
    const { formValues, updateField } = this.props;
    const manualAspectRatio = _.get(formValues, `${MENU_ID.PRODUCT_IMAGE}.manualAspectRatio`, '');
    if (!isRatioV2Bool(manualAspectRatio)) {
      updateField(`setting.${MENU_ID.PRODUCT_IMAGE}.manualAspectRatio`, '');
    }
  }

  render() {
    const { i18n, formValues } = this.props;

    const formattedProductImageRatio = PRODUCT_IMAGE_RATIO_OPTIONS_SI.map(function (option) {
      return {
        ...option,
        label: i18n.translate(option.label)
      };
    });
    const formattedCropImagePosition = PRODUCT_IMAGE_CROP_IMAGE_POSITION_OPTIONS.map(function (
      option
    ) {
      return {
        ...option,
        label: i18n.translate(option.label)
      };
    });
    const formattedHoverEffect = PRODUCT_IMAGE_HOVER_EFFECT_OPTIONS.map(function (option) {
      return {
        ...option,
        label: i18n.translate(option.label)
      };
    });

    const formattedProductInfoAlignment = PRODUCT_INFORMATION_ALIGNMENT_OPTIONS_TEXT.map(function (
      option
    ) {
      return {
        ...option,
        label: i18n.translate(option.label)
      };
    });

    const productImageRatio = _.get(
      formValues,
      `setting.${MENU_ID.PRODUCT_IMAGE}.aspectRatioType`,
      ''
    );

    return (
      <FormLayout>
        <div>
          <span className="form-link">
            <TextStyle variation="subdued">
              {i18n.translate(
                'AdminHTML.ShopifyIntegrationSettings.Form.productItemLayout_UserGuide'
              )}
            </TextStyle>{' '}
            <Link url={URL['ProductItemLayoutShopifyIntegration']} external>
              {i18n.translate('AdminHTML.ThemeSetting.Common.clickHere')}
              <Icon source={ExternalMinor} color="base" />
            </Link>
            <span style={{ color: '#2C6ECB' }}>.</span>
          </span>
        </div>

        <div className="form-section">
          {/* Product image aspect ratio */}
          <div className="form-section__item">
            <Field
              name={`setting.${MENU_ID.PRODUCT_IMAGE}.aspectRatioType`}
              label={i18n.translate('AdminHTML.ThemeSetting.Form.productImage_Ratio_Item')}
              options={formattedProductImageRatio}
              component={RenderSelect2}
              components={{ DropdownIndicator }}
              styles={styles}
              isSearchable={false}
              maxHeight={250}
              onChange={this.handleChangeRatio}
            />
            <div style={{ marginTop: '4px' }}>
              <TextStyle variation="subdued">
                {i18n.translate('AdminHTML.ThemeSetting.Form.productImage_Ratio_Description1')}
              </TextStyle>{' '}
              <a
                className="form-link"
                href="https://help.shopify.com/en/manual/shopify-admin/productivity-tools/image-editor#image-aspect-ratio"
                target="_blank"
                rel="noopener noreferrer">
                {i18n.translate('AdminHTML.ThemeSetting.Form.productImage_Ratio_Description2')}
                <Icon source={ExternalMinor} color="base" />
              </a>
              <span style={{ color: '#2C6ECB' }}>.</span>
            </div>
          </div>

          {/* Manual ratio */}
          {productImageRatio === PRODUCT_IMAGE_RATIO_VALUE.MANUAL && (
            <div className="form-section__item form-section__item--medium-spacing">
              <Field
                name={`setting.${MENU_ID.PRODUCT_IMAGE}.manualAspectRatio`}
                label={i18n.translate('AdminHTML.ThemeSetting.Form.productImage_ManualRatio_Item')}
                component={RenderTextField}
                placeholder={i18n.translate(
                  'AdminHTML.ThemeSetting.Form.productImage_ManualRatio_Placeholder'
                )}
                validate={[isRatioV2]}
              />
            </div>
          )}

          {/* Crop image position */}
          {productImageRatio !== PRODUCT_IMAGE_RATIO_VALUE.NATURAL &&
            productImageRatio !== PRODUCT_IMAGE_RATIO_VALUE.AUTO && (
              <div className="form-section__item form-section__item--medium-spacing">
                <Field
                  name={`setting.${MENU_ID.PRODUCT_IMAGE}.cropPosition`}
                  label={i18n.translate(
                    'AdminHTML.ThemeSetting.Form.productImage_CropImagePosition_Item'
                  )}
                  options={formattedCropImagePosition}
                  component={RenderSelect2}
                  components={{ DropdownIndicator }}
                  styles={styles}
                  isSearchable={false}
                />
              </div>
            )}

          {/* Hover effect */}
          <div className="form-section__item form-section__item--medium-spacing">
            <Field
              name={`setting.${MENU_ID.PRODUCT_IMAGE}.hoverEffect`}
              label={i18n.translate('AdminHTML.ThemeSetting.Form.productImage_HoverEffect_Item')}
              options={formattedHoverEffect}
              component={RenderSelect2}
              components={{ DropdownIndicator }}
              styles={styles}
              isSearchable={false}
            />
          </div>

          {/* Product information alignment */}
          <div className="form-section__item form-section__item--medium-spacing">
            <p className="title">
              {i18n.translate(
                'AdminHTML.ShopifyIntegrationSettings.Form.productInformation_Alignment_Item'
              )}
            </p>
            <div className="radio-button-wrapper radio-button-wrapper--no-gap radio-button-wrapper--no-radius-middle">
              {formattedProductInfoAlignment.map((item, index) => (
                <Field
                  key={index}
                  name={`setting.${MENU_ID.PRODUCT_INFORMATION}.textAlign`}
                  id={item.value}
                  component={RenderRadioButton2}
                  label={item.label}
                />
              ))}
            </div>
          </div>
        </div>
      </FormLayout>
    );
  }
}

export default withI18n()(ProductImageForm);
